import { useEffect, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../Icon';

const ICONS: Record<'info' | 'warning' | 'danger', IconName> = { info: 'info', warning: 'alert', danger: 'alertcircle' };

const format = (seconds: number): string => String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0');

interface AlertProps {
  tone?: 'info' | 'warning' | 'danger';
  icon?: IconName;
  title?: string;
  children: ReactNode;
  /** Décompte en secondes affiché `mm:ss` ; `onEnd` est appelé à zéro. */
  countdown?: number;
  onEnd?: () => void;
}

/** Bandeau d'alerte en ligne (design system : `Alert`), avec décompte optionnel. */
export function Alert({ tone = 'info', icon, title, children, countdown, onEnd }: AlertProps) {
  // `countdown` ne sert qu'à l'initialisation : pour relancer le décompte, remonter le composant (`key`).
  const [left, setLeft] = useState(countdown);
  useEffect(() => {
    if (left === undefined) return;
    if (left <= 0) {
      onEnd?.();
      return;
    }
    const timer = setTimeout(() => setLeft((l) => (l === undefined ? l : l - 1)), 1000);
    return () => clearTimeout(timer);
  }, [left, onEnd]);
  return (
    <div className={'lh-alert lh-alert-' + tone} role={tone === 'info' ? 'status' : 'alert'}>
      <Icon name={icon ?? ICONS[tone]} size={20} />
      <div className="lh-alert-b">
        {title ? <strong className="lh-alert-t">{title}</strong> : null}
        <span>
          {children}
          {left !== undefined ? <> <b className="lh-alert-time">{format(Math.max(0, left))}</b></> : null}
        </span>
      </div>
    </div>
  );
}
